/**
 * ProjectsPage - Listagem do módulo de Projetos (Helpdesk)
 *
 * Anatomia do Design System (primitivos da Fase 2):
 * toolbar de 3 zonas (breadcrumb + count pill · `ToolbarSearch` centralizada ·
 * `FiltersButton` popover claro-fixo + `ViewToggle` Lista/Cards + único primário
 * `Button collapsibleLabel`) → filterbar de chips removíveis (some quando vazia)
 * → bulk bar (seleção SÓ na tabela — decisão F34: cards sem checkbox) → tabela
 * (`ProjectListRow`, head via `SortableHeadCell`/`PlainHeadCell`) OU cards com
 * mini-Gantt (`ProjectCard`); mobile usa cards. Busca server-side (debounce),
 * status/pipeline/líder no query, ordenação client-side na página carregada.
 * O modo de visualização e o filtro de status são persistidos via
 * `useProjectsPreferences` (localStorage).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  DeleteConfirmationModal,
  FilterBottomSheet,
  FiltersButton,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

import { ProjectCard } from '../components';
import { ProjectListRow } from '../components/ProjectListRow';
import { useProjects, useProjectsPreferences, useUpdateProject, useDeleteProject } from '../hooks';
import type {
  ProjectListItem,
  ProjectQuery,
  ProjectStatus,
  ProjectsStatusFilter,
} from '../types';

interface UserLite {
  id: string;
  name: string;
  avatar?: string;
}

type SortKey = 'name' | 'progress' | 'period';

const PAGE_SIZE = 12;

/** Opções do filtro de status (ordem: Ativos default → Todos → Concluídos → Arquivados). */
const STATUS_OPTIONS: { value: ProjectsStatusFilter; label: string }[] = [
  { value: 'active', label: 'Ativos' },
  { value: 'all', label: 'Todos' },
  { value: 'completed', label: 'Concluídos' },
  { value: 'archived', label: 'Arquivados' },
];

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();

  // Preferências persistidas (localStorage): view mode + filtro de status.
  const { viewMode, statusFilter, setViewMode, setStatusFilter } = useProjectsPreferences();

  // Busca (input imediato + valor debounced que alimenta a query).
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Filtros adicionais (não persistidos) + estado da UI de listagem.
  const [pipelineFilter, setPipelineFilter] = useState<string | undefined>(undefined);
  const [leaderFilter, setLeaderFilter] = useState<string | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | 'bulk' | null>(null);

  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // "+" central do bottom-nav mobile → novo projeto (mockup .m-save).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/helpdesk/projects/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);

  // Reset de paginação + seleção quando busca ou filtros mudam.
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [debouncedSearch, statusFilter, pipelineFilter, leaderFilter]);

  const statusForQuery: ProjectStatus | undefined =
    statusFilter === 'all' ? undefined : statusFilter;

  const query: ProjectQuery = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(statusForQuery ? { status: statusForQuery } : {}),
      ...(pipelineFilter ? { pipelineId: pipelineFilter } : {}),
      ...(leaderFilter ? { leaderId: leaderFilter } : {}),
    }),
    [page, debouncedSearch, statusForQuery, pipelineFilter, leaderFilter],
  );

  const { data, isLoading, error } = useProjects(query);

  // Resolvers cross-module (rotas dropdown, sem permissionMiddleware).
  const { data: pipelines } = usePipelinesDropdown();
  const { data: usersData } = useUsersDropdown();

  const pipelinesById = useMemo(() => {
    const m = new Map<string, string>();
    (pipelines ?? []).forEach((p) => m.set(p.id, p.name));
    return m;
  }, [pipelines]);

  const usersById = useMemo(() => {
    const m = new Map<string, UserLite>();
    (usersData ?? []).forEach((u) => m.set(u.id, { id: u.id, name: u.name, avatar: u.avatar }));
    return m;
  }, [usersData]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Ordenação client-side na página carregada (backend não expõe sort de projetos).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'progress') cmp = (a.progress?.pct ?? 0) - (b.progress?.pct ?? 0);
      else cmp = new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = sortedItems.length > 0 && sortedItems.every((p) => selectedIds.has(p.id));
  const someOnPageSelected = sortedItems.some((p) => selectedIds.has(p.id));

  // ── Handlers ──
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) sortedItems.forEach((p) => next.delete(p.id));
      else sortedItems.forEach((p) => next.add(p.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const openProject = (id: string) => navigate(`/helpdesk/projects/${id}`);
  const goNewProject = () => navigate('/helpdesk/projects/new');

  const archiveToggle = (id: string, status: ProjectStatus) =>
    updateProject.mutate({ id, data: { status: status === 'archived' ? 'active' : 'archived' } });

  const handleDelete = (id: string) => {
    const project = sortedItems.find((p) => p.id === id) ?? null;
    if (project) setDeleteTarget(project);
  };

  const confirmDelete = () => {
    if (deleteTarget === 'bulk') {
      Array.from(selectedIds).forEach((id) => deleteProject.mutate(id));
      clearSelection();
    } else if (deleteTarget) {
      deleteProject.mutate(deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  const bulkArchive = () => {
    sortedItems
      .filter((p) => selectedIds.has(p.id) && p.status !== 'archived')
      .forEach((p) => updateProject.mutate({ id: p.id, data: { status: 'archived' } }));
    clearSelection();
  };

  const bulkDelete = () => setDeleteTarget('bulk');

  const clearAllFilters = () => {
    setStatusFilter('active');
    setPipelineFilter(undefined);
    setLeaderFilter(undefined);
    setFiltersOpen(false);
  };

  // ── Chips de filtros aplicados (status default 'active' não gera chip) ──
  const statusLabel =
    statusFilter !== 'active' ? (STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? null) : null;
  const pipelineLabel = pipelineFilter ? (pipelinesById.get(pipelineFilter) ?? null) : null;
  const leaderLabel = leaderFilter ? (usersById.get(leaderFilter)?.name ?? null) : null;
  const activeFilterCount = [statusLabel, pipelineLabel, leaderLabel].filter(Boolean).length;

  const hasFiltersOrSearch = activeFilterCount > 0 || !!debouncedSearch;

  // Botão de opção do popover de filtros (tema claro fixo).
  const FilterOption: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
    active,
    onClick,
    children,
  }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
      style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <span className="truncate">{children}</span>
      {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
    </button>
  );

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      {/* Status */}
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {STATUS_OPTIONS.map((opt) => (
        <FilterOption
          key={opt.value}
          active={statusFilter === opt.value}
          onClick={() => { setStatusFilter(opt.value); setFiltersOpen(false); }}
        >
          {opt.label}
        </FilterOption>
      ))}

      {/* Pipeline */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Pipeline</p>
      <FilterOption active={!pipelineFilter} onClick={() => { setPipelineFilter(undefined); setFiltersOpen(false); }}>
        Todos
      </FilterOption>
      {(pipelines ?? []).map((p) => (
        <FilterOption
          key={p.id}
          active={pipelineFilter === p.id}
          onClick={() => { setPipelineFilter(p.id); setFiltersOpen(false); }}
        >
          {p.name}
        </FilterOption>
      ))}

      {/* Líder */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Líder</p>
      <FilterOption active={!leaderFilter} onClick={() => { setLeaderFilter(undefined); setFiltersOpen(false); }}>
        Todos
      </FilterOption>
      {(usersData ?? []).map((u) => (
        <FilterOption
          key={u.id}
          active={leaderFilter === u.id}
          onClick={() => { setLeaderFilter(u.id); setFiltersOpen(false); }}
        >
          {u.name}
        </FilterOption>
      ))}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/helpdesk/tickets')}
              className="text-sm font-medium truncate hidden sm:inline transition-colors"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
              data-testid="projects-breadcrumb-helpdesk"
            >
              Help Desk
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-sm truncate" style={{ color: 'var(--theme-text)' }}>Projetos</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="projects-count"
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-F22: ToolbarSearch, 360px) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar projetos…"
            shrinkClass="ds-search"
            data-testid="projects-search"
          />
        }
        right={
          <>
            {/* Direita (ordem canônica F15): Filtros → Toggle → separador → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="projects-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="projects-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-F11: Lista primeiro, grid = "Cards") */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="projects-viewmode"
          />

          <span className="w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo projeto (único primário — F01-F03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={goNewProject}
            data-testid="projects-page-create"
          >
            Novo projeto
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar projeto…"
              data-testid="projects-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="projects-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter('active')} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status" data-testid="projects-chip-status-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {pipelineLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Pipeline: {pipelineLabel}
              <button onClick={() => setPipelineFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de pipeline" data-testid="projects-chip-pipeline-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {leaderLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Líder: {leaderLabel}
              <button onClick={() => setLeaderFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de líder" data-testid="projects-chip-leader-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="projects-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar projetos: {(error as Error).message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando projetos..." />}

          {!isLoading && !error && sortedItems.length === 0 && (
            <div data-testid="projects-empty-state">
              <ListPageEmpty
                icon="folder"
                message="Nenhum projeto encontrado"
                description={
                  hasFiltersOrSearch
                    ? 'Tente ajustar a busca ou os filtros.'
                    : 'Crie seu primeiro projeto para organizar tarefas em um cronograma integrado ao helpdesk'
                }
                actionLabel={hasFiltersOrSearch ? undefined : 'Criar o primeiro projeto'}
                onAction={hasFiltersOrSearch ? undefined : goNewProject}
              />
            </div>
          )}

          {/* ── Bulk bar ── */}
          {selectedCount > 0 && (
            <div className="flex items-center gap-2.5 px-4 py-2 mb-3 rounded-lg" style={{ backgroundColor: 'var(--theme-primary-background)', border: '1px solid var(--theme-primary)' }} data-testid="projects-bulk-bar">
              <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount === 1 ? '1 selecionado' : `${selectedCount} selecionados`}</span>
              <span className="flex-1" />
              <button onClick={bulkArchive} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="projects-bulk-archive">
                <Icon name="archive" className="w-3.5 h-3.5" /> Arquivar
              </button>
              <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="projects-bulk-delete">
                <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
              </button>
              <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="projects-bulk-clear">Limpar seleção</button>
            </div>
          )}

          {/* Grid (Cards) */}
          {!isLoading && !error && sortedItems.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-[14px]">
              {sortedItems.map((project: ProjectListItem) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  pipelineName={pipelinesById.get(project.pipelineId)}
                  usersById={usersById}
                  onOpen={() => openProject(project.id)}
                  onArchiveToggle={archiveToggle}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && sortedItems.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((project: ProjectListItem) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    pipelineName={pipelinesById.get(project.pipelineId)}
                    usersById={usersById}
                    onOpen={() => openProject(project.id)}
                    onArchiveToggle={archiveToggle}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" style={{ minWidth: 860 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                        <SelectAllHeadCell
                          checked={allOnPageSelected}
                          indeterminate={someOnPageSelected && !allOnPageSelected}
                          onToggle={toggleSelectAllOnPage}
                          data-testid="projects-select-all"
                        />
                        <SortableHeadCell label="Projeto" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                        <PlainHeadCell label="Pipeline" />
                        <PlainHeadCell label="Líder" />
                        <SortableHeadCell label="Progresso" sortKey="progress" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                        <SortableHeadCell label="Período" sortKey="period" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                        <PlainHeadCell label="Próximo marco" />
                        <PlainHeadCell label="Status" />
                        <PlainHeadCell className="w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((project: ProjectListItem) => (
                        <ProjectListRow
                          key={project.id}
                          project={project}
                          pipelineName={pipelinesById.get(project.pipelineId)}
                          leader={usersById.get(project.leaderId)}
                          selected={selectedIds.has(project.id)}
                          onToggleSelect={toggleSelect}
                          onOpen={() => openProject(project.id)}
                          onArchiveToggle={archiveToggle}
                          onDelete={handleDelete}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Paginação — pager numerado sempre visível com itens (mockup) */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(p) => setPage(p)}
              totalItems={data.total}
              pageSize={PAGE_SIZE}
              itemLabel="projetos"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="projects-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <DeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Excluir projeto"
        message={
          deleteTarget === 'bulk'
            ? `Excluir ${selectedCount} projeto(s) selecionado(s)?`
            : `Excluir o projeto "${deleteTarget?.name ?? ''}"?`
        }
        warningMessage="As tarefas são desvinculadas, mas os tickets permanecem no helpdesk. Esta ação não pode ser desfeita."
        confirmButtonText="Excluir"
        isLoading={deleteProject.isPending}
      />
    </div>
  );
};
